'use client'

import Button from '@eversports/klimt-primitives/alpha/Button'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { Sheet, SheetTrigger } from '../../../../../../../../components/Sheet'
import { FILTER_KEY_URL_PARAMS, FILTER_TYPE } from '../Filters.types'
import { BRAND_SELECTED_FILTER_ICON, FILTER_ICON } from '../../../../../../../App.icons'

import FilterSheetGroup from './FilterSheetGroup'
import FilterSheetContent from './FilterSheetContent'
import { UpdateFiltersProps } from './AdditionalFilters'

interface Props {
  selectedFilters: { [key in FILTER_TYPE]: Array<string> }
  resetFilter: (type: FILTER_TYPE) => () => void
  resetAllFilters: () => void
  handleApply: () => void
  updateFilters: (values: UpdateFiltersProps) => () => void
  undoFilterSelection: () => void
}

const FiltersSheet = ({
  selectedFilters,
  handleApply,
  updateFilters,
  resetFilter,
  resetAllFilters,
  undoFilterSelection,
}: Props) => {
  const t = useTranslations('discover-sport-city')

  const [isOpen, setIsOpen] = useState(false)

  const searchParams = useSearchParams()
  const params = new URLSearchParams(searchParams.toString())

  const activeFiltersInSheet = new Set(params.keys())
  // Remove date and start time because they're not part of the Filter Sheet - and shouldn't be part of the "count"
  activeFiltersInSheet.delete(FILTER_KEY_URL_PARAMS.DATE)
  activeFiltersInSheet.delete(FILTER_KEY_URL_PARAMS.START_TIME)

  const activeFiltersInSheetCount = activeFiltersInSheet.size
  const hasActiveFilters = activeFiltersInSheetCount > 0

  const filterIcon = hasActiveFilters ? BRAND_SELECTED_FILTER_ICON : FILTER_ICON

  const toggleSheet = () => {
    setIsOpen(!isOpen)
    undoFilterSelection()
  }

  const handleClick = () => {
    handleApply()
    toggleSheet()
  }

  const handleResetAll = () => {
    resetAllFilters()
    toggleSheet()
  }

  return (
    <Sheet open={isOpen} onOpenChange={toggleSheet}>
      <SheetTrigger asChild className="sm:hidden">
        <div className="relative">
          <Button variant={hasActiveFilters ? 'tertiary-selected' : 'outline'} className="flex items-center gap-1">
            <Image src={filterIcon} width={12} height={12} alt="" loading="lazy" />
            {t('filters')}
          </Button>
          {/* This is a notification count badge - good candidate to add to the Badge prototype when that PR is in */}
          {hasActiveFilters && (
            <div className="absolute right-[-4px] top-[-4px] rounded-lg bg-surface-brand-selected px-[6px] text-xs font-medium text-white">
              {activeFiltersInSheetCount}
            </div>
          )}
        </div>
      </SheetTrigger>
      <FilterSheetContent reset={handleResetAll}>
        <div className="flex h-full flex-col items-center justify-between gap-2">
          <div className="flex flex-col gap-4">
            <FilterSheetGroup
              filterType={FILTER_TYPE.LOCATION_OFFERING}
              activeFilters={selectedFilters[FILTER_TYPE.LOCATION_OFFERING]}
              updateFilters={updateFilters}
              resetFilter={resetFilter}
            />
            <FilterSheetGroup
              filterType={FILTER_TYPE.LEVEL}
              activeFilters={selectedFilters[FILTER_TYPE.LEVEL]}
              updateFilters={updateFilters}
              resetFilter={resetFilter}
            />
          </div>
          <Button onClick={handleClick} className="w-full">
            {t('apply-changes')}
          </Button>
        </div>
      </FilterSheetContent>
    </Sheet>
  )
}
export default FiltersSheet
